// Checkbox + status hint shown directly under the listen player.
// Three visible states:
//   - 'uncached' (red):  "Soll der Track offline verfuegbar sein?"
//                        unchecked checkbox -> click triggers download
//   - 'caching':         "Wird heruntergeladen..."
//   - 'cached'  (green): "Track ist offline verfuegbar"
//
// Hidden when OFFLINE_ENABLED is false or when the URL is empty.

import { useEffect, useMemo, useState } from 'react'
import { OFFLINE_ENABLED } from '../../config/offline'
import {
  cacheListenUrl,
  isListenUrlCached,
  type CacheProgress,
} from '../../lib/offline/listenCache'
import { formatBytes } from '../../lib/offline/quota'

interface ListenCachePromptProps {
  url: string
}

type Phase = 'unknown' | 'uncached' | 'caching' | 'cached' | 'error' | 'unsupported'

// Lossless formats are intentionally excluded from offline caching.
// Keep this list in sync with the Workbox runtimeCaching regex in
// vite.config.ts.
const CACHEABLE_AUDIO_RE = /\.(mp3|ogg|aac|m4a|opus|webm)(\?|$)/i

function isCacheable(url: string): boolean {
  try {
    return CACHEABLE_AUDIO_RE.test(new URL(url).pathname)
  } catch {
    return CACHEABLE_AUDIO_RE.test(url)
  }
}

export function ListenCachePrompt({ url }: ListenCachePromptProps) {
  const [phase, setPhase] = useState<Phase>('unknown')
  const [errorMsg, setErrorMsg] = useState<string | null>(null)
  const [progress, setProgress] = useState<CacheProgress | null>(null)

  const supported = useMemo(() => isCacheable(url), [url])

  useEffect(() => {
    if (!OFFLINE_ENABLED || !url) {
      setPhase('unknown')
      return
    }

    if (!supported) {
      setPhase('unsupported')
      return
    }

    let cancelled = false
    isListenUrlCached(url).then((cached) => {
      if (cancelled) return
      setPhase(cached ? 'cached' : 'uncached')
    })

    return () => {
      cancelled = true
    }
  }, [url, supported])

  if (!OFFLINE_ENABLED || !url || phase === 'unknown') return null

  async function handleToggle(checked: boolean) {
    if (!checked) return
    setPhase('caching')
    setErrorMsg(null)
    setProgress({ receivedBytes: 0, totalBytes: 0, percent: 0 })
    try {
      await cacheListenUrl(url, (p) => setProgress(p))
      setPhase('cached')
      setProgress(null)
    } catch (err) {
      console.error('[offline] listen cache failed:', err)
      setErrorMsg(err instanceof Error ? err.message : String(err))
      setPhase('error')
      setProgress(null)
    }
  }

  if (phase === 'cached') {
    return (
      <div className="mt-1 flex items-center gap-2 text-xs text-success">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
        Track ist offline verfuegbar
      </div>
    )
  }

  if (phase === 'caching') {
    const percent = progress?.percent ?? -1
    const received = progress?.receivedBytes ?? 0
    const total = progress?.totalBytes ?? 0

    return (
      <div className="mt-1 flex flex-col gap-1">
        <div className="flex items-center gap-2 text-xs text-text-muted">
          {percent >= 0 ? (
            <span className="font-mono text-text">{percent}%</span>
          ) : (
            <span className="inline-block animate-spin">o</span>
          )}
          <span>
            Wird heruntergeladen
            {total > 0 && (
              <>
                {' '}
                ({formatBytes(received)} / {formatBytes(total)})
              </>
            )}
          </span>
        </div>
        {percent >= 0 && (
          <div className="h-1 w-full overflow-hidden rounded-full bg-surface-lighter">
            <div
              className="h-full bg-primary transition-all"
              style={{ width: `${percent}%` }}
            />
          </div>
        )}
      </div>
    )
  }

  if (phase === 'error') {
    return (
      <div className="mt-1 flex flex-col gap-1 text-xs text-error">
        <span>Download fehlgeschlagen</span>
        {errorMsg && <span className="text-text-muted">{errorMsg}</span>}
      </div>
    )
  }

  if (phase === 'unsupported') {
    return (
      <div className="mt-1 text-xs text-text-muted">
        Format nicht offline-faehig (nur mp3, ogg, aac, m4a, opus, webm)
      </div>
    )
  }

  // 'uncached'
  return (
    <label className="mt-1 flex cursor-pointer items-center gap-2 text-xs text-error">
      <input
        type="checkbox"
        className="accent-error"
        checked={false}
        onChange={(e) => handleToggle(e.target.checked)}
      />
      Soll der Track offline verfuegbar sein?
    </label>
  )
}
